import {coachingApi, dashboardApi, triggersApi} from './api';
import {List, Map} from 'immutable';
import Team from 'records/Team';
import {shouldApplyDeptFilter} from 'sources/ManagerDashboardSource';
import zipWith from 'lodash/zipWith';
import {resolveMetricTeamFilter} from 'actions/ExecDashboard.utils';

const dateFormat = 'YYYY-MM-DD';

export default {
  getTrendData({teamId, direction, type, startDate, endDate, additionalTeamIds, context}) {
    const teams = teamId ? [teamId, ...additionalTeamIds] : additionalTeamIds;
    return dashboardApi.getJson(`/trend/team/${type}`, {
      params: {
        startDate: startDate.format(dateFormat),
        endDate: endDate.format(dateFormat),
        ...(direction && {direction}),
        ...(context && {context}),
        ...resolveMetricTeamFilter(teams),
      },
    });
  },

  getTopicTrendPercentData({teamId, startDate, endDate, additionalTeamIds, context}) {
    const topicDataPromise = this.getTrendData({
      teamId,
      type: 'trigger',
      startDate,
      endDate,
      additionalTeamIds,
      context,
    });
    const conversationDataPromise = this.getTrendData({
      teamId,
      type: 'conversations',
      startDate,
      endDate,
      additionalTeamIds,
    });

    return Promise.all([topicDataPromise, conversationDataPromise]).then(
      ([topicData, conversationData]) => {
        const zippedData = Object.entries(topicData).map(([k, v]) => [
          k,
          zipWith(v.data, conversationData[k].data, (t, c) => {
            if (t.value === null && c.value === null) {
              return {date: t.date, value: null};
            } else if (t.value === null && c.value !== null) {
              return {date: t.date, value: 0};
            } else if (t.value !== null && c.value === null) {
              console.warn(`topic data without conversation data for ${t.date}, shouldnt happen`);
              return {date: t.date, value: 100};
            }

            return {date: t.date, value: (t.value / c.value) * 100};
          }),
        ]);
        return Object.fromEntries(zippedData.map(([k, v]) => [k, {data: v}]));
      }
    );
  },

  getInterchanges({teams, startDate, endDate}) {
    return dashboardApi
      .getJson(`/metrics/team/interchanges`, {
        params: {
          startDate: startDate.format(dateFormat),
          endDate: endDate.format(dateFormat),
          stat: 'PER',
          ...resolveMetricTeamFilter(teams),
        },
      })
      .then(r => Map(r.stats.map(c => [c.id, c.value])));
  },

  getTalkListen({teams, startDate, endDate}) {
    return dashboardApi
      .getJson(`/metrics/team/talklisten`, {
        params: {
          startDate: startDate.format(dateFormat),
          endDate: endDate.format(dateFormat),
          stat: 'PER',
          ...resolveMetricTeamFilter(teams),
        },
      })
      .then(r => Map(r.stats.map(c => [c.id, c.value])));
  },

  getConversations({teams, startDate, endDate}) {
    return dashboardApi
      .getJson(`/metrics/team/conversations`, {
        params: {
          startDate: startDate.format(dateFormat),
          endDate: endDate.format(dateFormat),
          stat: 'PER',
          ...resolveMetricTeamFilter(teams),
        },
      })
      .then(r => Map(r.stats.map(c => [c.id, c.value])));
  },

  getDashboardTriggers(teamIds) {
    const params = teamIds && {
      teamId: teamIds,
    };

    return triggersApi
      .getJson(`/triggers/dashboard`, {
        params,
      })
      .then(r => Map(r.map(nameAndId => [nameAndId.id, nameAndId.name])));
  },

  getTeamsInAccount() {
    return coachingApi
      .getJson(`/teams/direct_reports`, {
        params: {deptFilter: shouldApplyDeptFilter()},
      })
      .then(data => Map(data.map(t => [t.id, new Team(t)])));
  },

  getCoachingTimeTrend({managerId, startDate, endDate}) {
    return dashboardApi.getJson(`/trend/manager/${managerId}/coaching-time`, {
      params: {
        startDate: startDate.format(dateFormat),
        endDate: endDate.format(dateFormat),
      },
    });
  },

  getTopicMetrics({teams, startDate, endDate}) {
    return dashboardApi
      .getJson(`/metrics/team/trigger`, {
        params: {
          startDate: startDate.format(dateFormat),
          endDate: endDate.format(dateFormat),
          stat: 'PER',
          ...resolveMetricTeamFilter(teams),
        },
      })
      .then(data => List(data.stats));
  },

  // Methods below have no corresponding actions. If you add them, adjust comments accordingly.

  /**
   * Get topic metrics for the user rollup.
   *
   * Note that this has no corresponding action, and thus does not write to the store.
   *
   * @param teams {Array} ids of teams. Will fetch stats for users in these teams.
   * @param startDate start date for metrics (inclusive).
   * @param endDate end date for metrics (exclusive).
   * @return {Promise} a promis resolving to an immutable list of stats.
   */
  getUserTopicMetrics({teams, startDate, endDate}) {
    return dashboardApi
      .getJson(`/metrics/users/trigger`, {
        params: {
          startDate: startDate.format(dateFormat),
          endDate: endDate.format(dateFormat),
          stat: 'PER',
          ...resolveMetricTeamFilter(teams),
        },
      })
      .then(data => List(data));
  },

  /**
   * Get user conversation metrics.
   *
   * Note that this has no corresponding action, and thus does not write to the store.
   *
   * @param teams {Array} ids of teams. Will fetch stats for users in these teams.
   * @param startDate start date for metrics (inclusive).
   * @param endDate end date for metrics (exclusive).
   * @return {List} list of stats.
   */
  getUserConversations({teams, startDate, endDate}) {
    return dashboardApi
      .getJson(`/metrics/users/conversations`, {
        params: {
          startDate: startDate.format(dateFormat),
          endDate: endDate.format(dateFormat),
          stat: 'PER',
          ...resolveMetricTeamFilter(teams),
        },
      })
      .then(r => Map(r.map(c => [c.id, c.value])));
  },

  getTeamMetric({teams = [], subject, direction, startDate, endDate, metric}) {
    return dashboardApi
      .getJson(`/metrics/${subject}/${metric}`, {
        params: {
          startDate: startDate.format(dateFormat),
          endDate: endDate.format(dateFormat),
          direction,
          stat: 'PER',
          ...resolveMetricTeamFilter(teams),
        },
      })
      .then(r => r.stats);
  },

  getUserMetric({teams = [], userId, direction, startDate, endDate, metric}) {
    return dashboardApi.getJson(`/metrics/user/${userId}/${metric}`, {
      params: {
        startDate: startDate.format(dateFormat),
        endDate: endDate.format(dateFormat),
        direction,
        stat: 'PER',
        ...resolveMetricTeamFilter(teams),
      },
    });
  },
};
